/**
 * The portal's one "please wait" surface.
 *
 * Mounted once, in `Shell`, and driven entirely by `loadingStore` — any layer
 * that starts a slow operation names what it is doing and this overlay says it
 * to the user, over a dimmed backdrop so the wait is unmistakable. The text is
 * the load-bearing half (it is what a screen reader announces and what
 * survives `prefers-reduced-motion`); the animation is warmth on top.
 *
 * The animation is the brand-recoloured Lottie in `loading-animation.json`,
 * played on a canvas: the canvas renderer touches no DOM styling, which is
 * what keeps it inside the `style-src 'self'` policy. The player is imported
 * lazily so no route pays its weight until the first genuine wait.
 */
import { useEffect, useRef, type RefObject } from 'react'

import { t, type MessageKey } from '../shell/i18n.js'
import { useLoading } from './loadingStore.js'
import animationUrl from './loading-animation.json?url'

import './loading.css'

interface LottieHandle {
  destroy(): void
}

function reducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function useLottie(host: RefObject<HTMLDivElement | null>): void {
  useEffect(() => {
    const element = host.current
    if (element === null || reducedMotion()) return

    let disposed = false
    let animation: LottieHandle | undefined
    void import('lottie-web/build/player/lottie_light_canvas.js').then((module) => {
      if (disposed) return
      animation = module.default.loadAnimation({
        container: element,
        renderer: 'canvas',
        loop: true,
        autoplay: true,
        path: animationUrl,
      })
    })

    return () => {
      disposed = true
      animation?.destroy()
    }
  }, [host])
}

function Overlay({ message }: { readonly message: MessageKey }) {
  const host = useRef<HTMLDivElement>(null)
  useLottie(host)

  return (
    <div className="loading-overlay">
      <div className="loading-overlay__panel" role="status" aria-live="polite">
        <div className="loading-overlay__animation" ref={host} aria-hidden="true" />
        <p className="loading-overlay__message">{t(message)}</p>
      </div>
    </div>
  )
}

export function LoadingOverlay() {
  const { active, message } = useLoading()

  return active ? <Overlay message={message} /> : null
}
